import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { AbstractService } from '../commons/abstract.service'
import { User } from '../models/user.model'
import { DEFAULT_LOCALE, isSupportedLocale, SUPPORTED_LOCALES, SupportedLocale } from './supported-locale'

export const LANGUAGE_STORAGE_KEY = 'trendmulti.language'

@Injectable({ providedIn: 'root' })
export class LanguageService extends AbstractService<User> {

  private readonly _currentLang$ = new BehaviorSubject<SupportedLocale>(DEFAULT_LOCALE)
  readonly currentLang$: Observable<SupportedLocale> = this._currentLang$.asObservable()

  constructor(http: HttpClient, private readonly translate: TranslateService) {
    super(http)
    // Seed from localStorage so LOCALE_ID (resolved once at bootstrap) already
    // reflects the persisted choice. translate.use() is applied in initFromUser.
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (isSupportedLocale(stored)) {
      this._currentLang$.next(stored)
    }
  }

  getURLBase(): string { return '/user' }
  preCreate(_obj: User): void { }
  preUpdate(_obj: User): void { }

  get currentLang(): SupportedLocale { return this._currentLang$.value }

  available(): SupportedLocale[] { return [...SUPPORTED_LOCALES] }

  /**
   * Apply a language to the running app (no reload) and, by default, persist it
   * to localStorage and to the backend (PUT /api/user/me/language).
   */
  setLanguage(lang: SupportedLocale, persist: boolean = true): void {
    const locale: SupportedLocale = isSupportedLocale(lang) ? lang : DEFAULT_LOCALE
    this.translate.use(locale)
    localStorage.setItem(LANGUAGE_STORAGE_KEY, locale)
    this._currentLang$.next(locale)
    if (persist) {
      this.put('/me/language', { language: locale }).subscribe({ error: () => { } })
    }
  }

  /**
   * Resolve the effective language at app bootstrap / login:
   * localStorage → User preference → Company default → pt-BR (RF-14).
   */
  initFromUser(user?: User): void {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    const resolved: SupportedLocale =
      (isSupportedLocale(stored) ? stored : undefined)
      ?? user?.language
      ?? user?.company?.language
      ?? DEFAULT_LOCALE
    this.setLanguage(resolved, false)
  }
}
